var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { matrix_pool, vector4_pool } from '../../Math.js';
import { Vector3 as BABYLON_Vector3, Vector4 as BABYLON_Vector4, Quaternion as BABYLON_Quaternion } from '@inter-ikea-betrakta-babylonjs/core/Maths/math.vector.js';
import { Observable } from '../../Observable.js';
import { RENDER_LAYER_MASK_DEFAULT } from '../RenderLayer.js';
import { Deprecated } from '../../utils/Deprecated.js';
import { Indexed } from '../../utils/IndexedName.js';
import { Debug } from '../../utils/DebugLayer/utils/DebugDecorators.js';
/**
  * Defining ViwerCamera is a generic class allows us to define obesrvables as Observable<T, ...>
  * This prevents potential TS error on assigning any inherited camera as a ViewerCamera
*/
let ViewerCamera = class ViewerCamera {
    constructor(viewer) {
        this.viewer = viewer;
        this._forward = new BABYLON_Vector3(0, 0, 0);
        this._backward = new BABYLON_Vector3(0, 0, 0);
        this._right = new BABYLON_Vector3(0, 0, 0);
        this._left = new BABYLON_Vector3(0, 0, 0);
        this._up = new BABYLON_Vector3(0, 0, 0);
        this._down = new BABYLON_Vector3(0, 0, 0);
        this._position = new BABYLON_Vector3(0, 0, 0);
        this._rotation = new BABYLON_Quaternion();
        this._view_matrix_cache = [
            { x: 1, y: 0, z: 0, w: 0 },
            { x: 0, y: 1, z: 0, w: 0 },
            { x: 0, y: 0, z: 1, w: 0 },
            { x: 0, y: 0, z: 0, w: 1 }
        ];
        this._disposed = false;
        this.dispose_observable = new Observable();
        /**
         * This observables needs to notify users whenever a user starts and stops interacting with a camera
         * It should however only be notified for continues changes and not when the user just sets a parameter
         */
        this.interaction_begin_observable = new Observable();
        this.interaction_end_observable = new Observable();
        /**
         * This observable notify users whenever a camera has been updated.
         * It should notify listeners only once per frame.
         */
        this.update_observable = new Observable();
    }
    /**
     * Note that this can point to the internal forward field and might not be a copy, must be normalized
     */
    get forward() {
        this.update_basis_and_position();
        return this._forward;
    }
    /**
   * Note that this can point to the internal backward field and might not be a copy, must be normalized
   */
    get backward() {
        this.update_basis_and_position();
        return this._backward;
    }
    /**
     * Note that this can point to the internal right field and might not be a copy, must be normalized
     */
    get right() {
        this.update_basis_and_position();
        return this._right;
    }
    /**
     * Note that this can point to the internal left field and might not be a copy, must be normalized
     */
    get left() {
        this.update_basis_and_position();
        return this._left;
    }
    /**
     * Note that this can point to the internal up field and might not be a copy, must be normalized
     */
    get up() {
        this.update_basis_and_position();
        return this._up;
    }
    /**
     * Note that this can point to the internal down field and might not be a copy, must be normalized
     */
    get down() {
        this.update_basis_and_position();
        return this._down;
    }
    /**
     * Note that this can point to the internal position field and might not be a copy
     * @important This is expected to give a correct value after dispose
     */
    get position() {
        this.update_basis_and_position();
        return this._position;
    }
    /**
     * Note that this can point to the internal rotation field and might not be a copy
     * The rotation is stored as a Quaternion
     */
    get rotation_quaternion() {
        this.update_basis_and_position();
        return this._rotation;
    }
    /**
     * @deprecated Use get_projection_matrix() instead
    */
    get projection_matrix() {
        return this.projection.get_projection_matrix();
    }
    get_projection_matrix(_custom_aspect_ratio) {
        // TODO: Make this function abstract in next breaking change (IC3V-1944)
        const advanced_viewer = this.viewer;
        if (advanced_viewer.get_config().verbose_warnings) {
            console.warn('get_projection_matrix not overloaded.');
        }
        return this.projection.get_projection_matrix();
    }
    get view_matrix() {
        this.update_basis_and_position(); // Make sure cache is up to date
        return this._view_matrix_cache;
    }
    get disposed() {
        return this._disposed;
    }
    dispose() {
        if (this.disposed)
            return;
        this._disposed = true;
        this.dispose_impl();
        this.dispose_observable.notify(this);
        // Clear observers
        this.dispose_observable.clear();
    }
    drag_start(evt) {
        // Does nothing by default
    }
    drag_end(evt) {
        // Does nothing by default
    }
    pan_start(evt) {
        // Does nothing by default
    }
    pan_end(evt) {
        // Does nothing by default
    }
    /** Called when camera is pushed to the Viewer camera stack. Useful for hooking up observers. */
    on_activation() {
        // Does nothing by default
    }
    /** Called when camera is popped from the Viewer camera stack. Use for example to unregister observers. */
    on_deactivation() {
        // Does nothing by default
    }
    /**
     * Takes a screenshot with the camera, using the camera's aspect ratio
     * @param vertical_resolution The vertical resolution of the screenshot. Width is calculated from the camera's aspect ratio.
     * @param settings General screenshot settings such as AO and output format. If no AO parameters are given the scene's current settings will be used.
     * @param layer_mask A RenderLayerMask describing which render layers to be included in the screenshot.
     * @returns A promise with the Base64-encoded screenshot data
     */
    take_screenshot(vertical_resolution, settings = {}, render_layer_mask = RENDER_LAYER_MASK_DEFAULT) {
        return this.viewer.take_screenshot(this.get_projection_matrix(), this.projection.get_camera_mode(), this.view_matrix, this.projection.get_aspect_ratio() * vertical_resolution, vertical_resolution, settings, render_layer_mask);
    }
    /**
     * Projects the given world position with the camera in its current state, and returns the x,y pixel coordinates with respect to the given width and height
     * @param world_position The world position to project
     * @param viewport_width The width of the viewport you're projecting on, such as the size of the canvas or resolution of a screenshot
     * @param viewport_height Same as viewport_width, but for height
     * @returns Returns null if the point is not visible to the camera's perspective, otherwise the pixel coordinates x,y (w.r.t. width & height)
     */
    project_world_position(world_position, viewport_width, viewport_height) {
        const view_matrix = matrix_pool.fetch();
        view_matrix.fromIMatrix4(this.view_matrix);
        const projection_matrix = matrix_pool.fetch();
        projection_matrix.fromIMatrix4(this.get_projection_matrix());
        const view_projection_matrix = view_matrix.multiplyToRef(projection_matrix, matrix_pool.fetch());
        const projected = BABYLON_Vector4.TransformCoordinatesFromFloatsToRef(world_position.x, world_position.y, world_position.z, view_projection_matrix, vector4_pool.fetch());
        projected.scaleInPlace(1.0 / projected.w);
        const ndc = {
            x: projected.x,
            y: projected.y,
            z: projected.z
        };
        matrix_pool.return(view_matrix, projection_matrix, view_projection_matrix);
        vector4_pool.return(projected);
        // Check if the position is outside the projection
        if (Math.abs(ndc.x) > 1.0 || Math.abs(ndc.y) > 1.0 || Math.abs(ndc.z) > 1.0) {
            return null;
        }
        return {
            x: (ndc.x * 0.5 + 0.5) * viewport_width,
            y: (1.0 - (ndc.y * 0.5 + 0.5)) * viewport_height
        };
    }
};
ViewerCamera.FORWARD = new BABYLON_Vector3(0, 0, -1.0);
ViewerCamera.UP = new BABYLON_Vector3(0, 1.0, 0);
ViewerCamera.RIGHT = new BABYLON_Vector3(1.0, 0, 0);
__decorate([
    Indexed
], ViewerCamera.prototype, "name", void 0);
__decorate([
    Deprecated({ new: 'ViewerCamera.get_projection_matrix()' })
], ViewerCamera.prototype, "projection_matrix", null);
ViewerCamera = __decorate([
    Debug.Persistent({ name: 'ViewerCamera', is_enabled: false })
], ViewerCamera);
export { ViewerCamera };
/**
 * Projects the given world position with the camera in its current state, and returns the x,y pixel coordinates with respect to the given width and height
 * @deprecated Use camera.project_world_position instead
 * @param camera The camera used to project
 * @param world_position The world position to project
 * @param viewport_width The width of the viewport you're projecting on, such as the size of the canvas or resolution of a screenshot
 * @param viewport_height Same as viewport_width, but for height
 * @returns Returns null if the point is not visible to the camera's perspective, otherwise the pixel coordinates x,y (w.r.t. width & height)
 */
export function project_world_position_from_camera(camera, world_position, viewport_width, viewport_height) {
    console.warn('Warning: project_world_position_from_camera is deprecated. Use camera.project_world_position instead.');
    return camera.project_world_position(world_position, viewport_width, viewport_height);
}
//# sourceMappingURL=ViewerCamera.js.map